/**
 * メンテナンス設定ページ
 * 全体メンテナンスのON/OFFのみを管理
 */

import React, { useEffect, useState } from "react";
import { AdminClient } from "../../../api/generated/APIClient";
import "./MaintenancePage.css";

// 全体メンテナンスのID
const ALL_MAINTENANCE_ID = "all";

// 無期限時の固定日付
const INDEFINITE_BEGIN_DATE = "2000-01-01T00:00:00Z";
const INDEFINITE_END_DATE = "2099-12-31T23:59:59Z";

export const MaintenancePage: React.FC = () => {
  const [isMaintenanceOn, setIsMaintenanceOn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [maintenanceExists, setMaintenanceExists] = useState(false);

  // Connect-ES APIクライアント呼び出し関数
  const callSystemEntityAPI = async (
    operation: "index" | "create" | "update",
    data?: any
  ) => {
    try {
      const entityPascalCase = "Maintenance";
      let methodName: string;
      switch (operation) {
        case "index":
          methodName = `Index${entityPascalCase}`;
          break;
        case "create":
          methodName = `Create${entityPascalCase}`;
          break;
        case "update":
          methodName = `Update${entityPascalCase}`;
          break;
        default:
          throw new Error(`未対応の操作: ${operation}`);
      }

      const apiMethod = (AdminClient as any)[methodName];
      if (!apiMethod || typeof apiMethod !== "function") {
        throw new Error(`APIメソッド ${methodName} が見つかりません`);
      }

      let apiArgs: any = {};
      if (operation === "index") {
        apiArgs = { offset: 0, limit: 0 };
      } else if (data) {
        apiArgs = { maintenances: [data] };
      }

      return await apiMethod(apiArgs);
    } catch (error) {
      console.error(`❌ ${operation} request failed:`, error);
      throw error;
    }
  };

  // メンテナンス状態を取得
  useEffect(() => {
    const fetchMaintenance = async () => {
      setIsLoading(true);
      try {
        const response = await callSystemEntityAPI("index");
        let items: any[] = [];
        if (response?.data) {
          if (Array.isArray(response.data)) {
            items = response.data;
          } else if (typeof response.data === "object") {
            const dataObj = response.data as any;
            if (dataObj.maintenances && Array.isArray(dataObj.maintenances)) {
              items = dataObj.maintenances;
            } else if (dataObj.items && Array.isArray(dataObj.items)) {
              items = dataObj.items;
            }
          }
        }
        // 全体メンテナンス（all）を取得
        const allMaintenance = items.find(
          (m) => m.maintenanceType === ALL_MAINTENANCE_ID || m.id === ALL_MAINTENANCE_ID
        );
        if (allMaintenance) {
          setMaintenanceExists(true);
          setIsMaintenanceOn(allMaintenance.isEnabled || false);
        } else {
          setMaintenanceExists(false);
          setIsMaintenanceOn(false);
        }
      } catch (error) {
        console.error("❌ Maintenance取得エラー:", error);
        setMaintenanceExists(false);
        setIsMaintenanceOn(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMaintenance();
  }, []);

  // メンテナンス状態を切り替え
  const toggleMaintenance = async () => {
    const newState = !isMaintenanceOn;
    const confirmMessage = newState
      ? "メンテナンスを開始しますか？\nサイトが一時的に利用できなくなります。"
      : "メンテナンスを終了しますか？\nサイトが再び利用可能になります。";

    if (!confirm(confirmMessage)) return;

    setIsSaving(true);
    try {
      const saveData = {
        id: ALL_MAINTENANCE_ID,
        maintenanceType: ALL_MAINTENANCE_ID,
        isEnabled: newState,
        isIos: true,
        isAndroid: true,
        beginDate: INDEFINITE_BEGIN_DATE,
        endDate: INDEFINITE_END_DATE,
      };

      if (maintenanceExists) {
        await callSystemEntityAPI("update", saveData);
      } else {
        await callSystemEntityAPI("create", saveData);
        setMaintenanceExists(true);
      }

      setIsMaintenanceOn(newState);
    } catch (error) {
      console.error("❌ 保存エラー:", error);
      alert(
        `保存に失敗しました: ${
          error instanceof Error ? error.message : "不明なエラー"
        }`
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="maintenance-page">
      <div className="maintenance-header">
        <h1>🔧 メンテナンス設定</h1>
      </div>

      {isLoading ? (
        <div className="maintenance-loading">読み込み中...</div>
      ) : (
        <div className="maintenance-simple-card">
          {/* ステータス表示 */}
          <div className={`maintenance-status ${isMaintenanceOn ? "on" : "off"}`}>
            <div className="status-indicator">
              <span className="status-icon">{isMaintenanceOn ? "🚧" : "✅"}</span>
              <div className="status-text">
                <h2>{isMaintenanceOn ? "メンテナンス中" : "通常稼働中"}</h2>
                <p>
                  {isMaintenanceOn
                    ? "現在サイトはメンテナンス状態です"
                    : "サイトは正常に稼働しています"}
                </p>
              </div>
            </div>
          </div>

          {/* ON/OFFボタン */}
          <div className="maintenance-action">
            <button
              className={`maintenance-toggle-btn ${isMaintenanceOn ? "stop" : "start"}`}
              onClick={toggleMaintenance}
              disabled={isSaving}
            >
              {isSaving ? (
                "処理中..."
              ) : isMaintenanceOn ? (
                <>
                  <span className="btn-icon">▶️</span>
                  メンテナンスを終了する
                </>
              ) : (
                <>
                  <span className="btn-icon">⏸️</span>
                  メンテナンスを開始する
                </>
              )}
            </button>
          </div>

          {/* 説明 */}
          <div className="maintenance-description">
            <p>
              {isMaintenanceOn
                ? "メンテナンス中は、一般ユーザーがサイトにアクセスできなくなります。作業が完了したら「メンテナンスを終了する」ボタンを押してください。"
                : "メンテナンスを開始すると、一般ユーザーはサイトにアクセスできなくなります。システム更新や緊急対応時にご使用ください。"}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
